/**
 * Data Transformation Hook
 * Transforms old gift data format to new format for compatibility
 */
import { useMemo } from 'react';
import { type FreeGiftsData } from '../../../types/front.graphql';

export interface UseDataTransformationProps {
    sortedGiftsDataList: FreeGiftsData[];
}

export interface UseDataTransformationReturn {
    availableGifts: FreeGiftsData[];
    isTransformed: boolean;
    transformationErrors: string[];
}

/**
 * Transform legacy gift data to new FreeGiftsData format
 */
const transformToFreeGiftsData = (legacyData: FreeGiftsData[]): FreeGiftsData[] => {
    return legacyData
        .filter(item => item.rule && item.products && item.products.length > 0)
        .map(item => ({
            rule: item.rule,
            products: item.products,
            // Map other required fields based on FreeGiftsData interface
            __typename: 'FreeGiftsData' as const,
        }));
};

/**
 * Hook to transform legacy gift data format to new format
 */
export const useDataTransformation = ({
    sortedGiftsDataList,
}: UseDataTransformationProps): UseDataTransformationReturn => {
    return useMemo(() => {
        const errors: string[] = [];

        try {
            // Validate input data
            if (!Array.isArray(sortedGiftsDataList)) {
                errors.push('Invalid gift data: not an array');
                return {
                    availableGifts: [],
                    isTransformed: false,
                    transformationErrors: errors,
                };
            }

            // Transform data
            const availableGifts = transformToFreeGiftsData(sortedGiftsDataList);

            // Validate transformation results
            const invalidItems = availableGifts.filter(gift => !gift.rule || !gift.products);
            if (invalidItems.length > 0) {
                errors.push(`${invalidItems.length} items failed validation after transformation`);
            }

            return {
                availableGifts,
                isTransformed: true,
                transformationErrors: errors,
            };
        } catch (error) {
            errors.push(`Transformation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);

            return {
                availableGifts: [],
                isTransformed: false,
                transformationErrors: errors,
            };
        }
    }, [sortedGiftsDataList]);
};
